// Canonical domain types shared across API, dashboard, and mobile.
// Generated types (from OpenAPI) will eventually live alongside these hand-written ones.

export type UUID = string;
export type ISODateString = string; // e.g. "2026-04-20T03:14:22Z"
export type E164Phone = string; // e.g. "+61412345678"

export type OrganisationType = 'customer' | 'partner' | 'platform';
export type OrganisationPlan = 'trial' | 'team' | 'business' | 'enterprise';
export type BillingStatus = 'trialing' | 'active' | 'past_due' | 'cancelled' | 'paused';
export type AppRole = 'worker' | 'monitor' | 'admin';
export type OrgRole = 'admin' | 'basic_member';

// Australian state/territory codes. Stored as the short code in users.state and
// surfaced alongside JobTitle as e.g. "VIC Physiotherapist". The DB enforces the
// controlled list via a CHECK constraint in migration 0012.
export type AustralianState = 'VIC' | 'NSW' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT';
export const AUSTRALIAN_STATES: readonly AustralianState[] = [
  'VIC',
  'NSW',
  'QLD',
  'WA',
  'SA',
  'TAS',
  'ACT',
  'NT',
] as const;

// Job title surfaced alongside the state on the worker badge (e.g.
// "VIC Physiotherapist"). Now per-organisation — the list of available titles
// lives in the `organisation_job_titles` table and is fetched at runtime; the
// stored value is just a free string.
//
// DEFAULT_JOB_TITLES is the seed list applied to every existing org by
// migration 0013 and to new orgs at org-creation time. Keep it stable —
// changing it does not retroactively rename or remove rows.
export type JobTitle = string;
export const DEFAULT_JOB_TITLES: readonly string[] = [
  'Physiotherapist',
  'Occupational Therapist',
  'Nurse',
  'Support Worker',
  'Speech Pathologist',
  'Social Worker',
  'Other',
] as const;

// OrganisationJobTitle — one row of the per-org job-title list. `source` records
// where the row came from: 'manual' (admin added or seeded default) or 'hris'
// (auto-added by an HRIS webhook). `is_active=false` archives the row — it stays
// in the database so historical worker badges keep rendering, but the Roster
// dropdown hides it.
export interface OrganisationJobTitle {
  id: UUID;
  organisation_id: UUID;
  name: string;
  source: 'manual' | 'hris';
  is_active: boolean;
  created_at: ISODateString;
  updated_at: ISODateString | null;
}

export type AlarmType = 'duress' | 'man_down' | 'overdue_check_in' | 'welfare_failed';
export type AlarmStatus = 'active' | 'acknowledged' | 'resolved' | 'false_alarm';

export type CheckInStatus = 'active' | 'completed' | 'overdue' | 'cancelled';

export type LocationSource = 'foreground' | 'background' | 'alarm' | 'check_in';

export type VisitStatus = 'scheduled' | 'arrived' | 'completed' | 'cancelled' | 'no_show';

export interface Team {
  id: UUID;
  organisation_id: UUID;
  name: string;
  slug: string;
  description: string | null;
  discipline: string | null;
  colour: string | null;
  member_count: number;
  created_at: ISODateString;
  updated_at: ISODateString | null;
}

export interface TeamMember {
  team_id: UUID;
  user_id: UUID;
  display_name: string;
  email: string;
  avatar_url: string | null;
  app_role: AppRole;
  joined_at: ISODateString;
}

// Team badge shown on the roster. A worker may belong to multiple teams.
export interface TeamChip {
  id: UUID;
  name: string;
  slug: string;
  colour: string | null;
}

export type RosterProvider = 'alayacare' | 'shiftcare' | 'brevity' | 'procura' | 'visualcare';
export type SmsProvider = 'clicksend' | 'twilio' | 'messagemedia' | 'platform_default';

export interface GeoPoint {
  lat: number;
  lng: number;
  accuracy_m?: number;
}

export interface Organisation {
  id: UUID;
  name: string;
  slug: string;
  type: OrganisationType;
  plan: OrganisationPlan;
  billing_status: BillingStatus;
  timezone: string;
  branding: OrganisationBranding;
  active: boolean;
}

export interface OrganisationBranding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  app_display_name?: string;
  email_from_name?: string;
  sms_sender_id?: string;
  dashboard_tagline?: string;
}

// Per-org alarm escalation policy. Stored in organisations.settings.escalation
// (jsonb); API applies defaults on read, so absent keys mean "escalation ON
// with default timings".
export interface AlarmEscalationSettings {
  enabled: boolean;
  // Unacknowledged for this long after trigger → first escalation re-notify.
  delay_seconds: number;
  repeat_interval_seconds: number;
  max_repeats: number;
  // Who the escalation re-notifies: every matching emergency contact, or only
  // organisation-scope contacts (the monitoring desk).
  target: 'all_contacts' | 'org_contacts';
}

export interface Alarm {
  id: UUID;
  organisation_id: UUID;
  user_id: UUID;
  type: AlarmType;
  status: AlarmStatus;
  triggered_at: ISODateString;
  acknowledged_at?: ISODateString;
  acknowledged_by?: UUID;
  resolved_at?: ISODateString;
  resolved_by?: UUID;
  resolution_note?: string;
  trigger_location?: GeoPoint;
  device_battery?: number;
  metadata?: Record<string, unknown>;
}

export interface CheckIn {
  id: UUID;
  organisation_id: UUID;
  user_id: UUID;
  scheduled_for: ISODateString;
  duration_minutes: number;
  status: CheckInStatus;
  completed_at?: ISODateString;
  location?: GeoPoint;
}

export interface Visit {
  id: UUID;
  organisation_id: UUID;
  user_id: UUID;
  provider: RosterProvider;
  external_visit_id: string;
  client_display_name?: string;
  service_type?: string;
  location_address?: string;
  location?: GeoPoint;
  scheduled_start: ISODateString;
  scheduled_end: ISODateString;
  actual_arrival?: ISODateString;
  actual_departure?: ISODateString;
  status: VisitStatus;
}

// Canonical error envelope — §5 conventions.
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// WS envelope — §6.1.
export interface WsEvent<T = unknown> {
  type: string;
  data: T;
  ts: ISODateString;
  seq?: number;
}
